import React, { useState } from 'react';
import {
  Box,
  Checkbox,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  Modal,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { hasSameContents } from '../../../../../utils/arrayUtils.js';
import axios from 'axios';
import { skillingSheetDropdownOptions } from '../skillingSheetUtils.js';
import { appInsights } from '../../../../../telemetry/appInsights.js';

export default function EditSkillingSheet({
  isOpen,
  handleClose,
  entry,
  handleRefresh,
  setIsSnackbarOpen,
  setSnackbarMessage,
  setSnackbarSeverity,
  user
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formState, setFormState] = useState({ ...entry });
  const [deleteSelected, setDeleteSelected] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const trackSkillRemoval = data => appInsights.trackEvent({ name: 'SkillRemoval' }, data);
  const trackSkillUpdate = data => appInsights.trackEvent({ name: 'SkillUpdate' }, data);

  const hideDialog = () => {
    setIsDialogOpen(false);
  };

  const hasChanges = () => {
    return !hasSameContents(Object.values(formState), Object.values(entry));
  };

  const calculateChange = () => {
    const changes = [];
    Object.keys(formState).forEach(key => {
      if (formState[key] !== entry[key]) {
        changes.push({ [key]: formState[key] === '1' ? 1 : formState[key] });
      }
    });
    console.log(changes)
    return changes;
  };

  const handleReset = () => {
    setFormState({ ...entry });
  };

  const removeSkills = () => {
    setIsDialogOpen(true);
  };

  async function removeSkillsConfirmed() {
    try {
      setIsSubmitting(true);
      const response = await axios({
        method: 'DELETE',
        url: `${process.env.REACT_APP_ENDPOINT_ADMIN}/workforce/skilling-sheet/remove`,
        data: { userId: formState.userId, deleteSelected }, // only send the changes instead of the entire entry
      });
      trackSkillRemoval({ skillRemovedBy: user, skillRemovedFor: formState.name, removedSkills: deleteSelected, response, sheetType: 'ES/Aurora' });
      setSnackbarMessage('Skills removed successfully');
      setSnackbarSeverity('success');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
      handleClose();
      handleRefresh();
    } catch (error) {
      console.error('Error removing skills:', error);
      setSnackbarMessage(
        'There was an error removing the skills. Please try again. If the problem persists, contact support.',
      );
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
    }
  }

  async function submit() {
    try {
      setIsSubmitting(true);
      const calculatedChanges = calculateChange();
      const response = await axios({
        method: 'PATCH',
        url: `${process.env.REACT_APP_ENDPOINT_ADMIN}/workforce/skilling-sheet/update`,
        data: { userId: formState.userId, changes: calculatedChanges }, // only send the changes instead of the entire entry
      });
      trackSkillUpdate({ skillUpdatedBy: user, skillUpdatedFor: formState.name, updatedSkills: calculatedChanges, response, sheetType: 'ES/Aurora' });
      setSnackbarMessage('ES/Aurora skilling sheet updated successfully');
      setSnackbarSeverity('success');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
      handleClose();
      handleRefresh();
    } catch (error) {
      console.error('Error submitting ES/Aurora skilling sheet edit:', error);
      setSnackbarMessage(
        'There was an error submitting the ES/Aurora skilling sheet update. Please try again. If the problem persists, contact support.',
      );
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <Modal
        open={isOpen}
        onClose={handleClose}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <Grid
          container
          spacing={2}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95vw', sm: '90vw', md: 800 },
            bgcolor: 'var(--dust-white)',
            p: 4,
            overflowY: 'auto',
            maxHeight: { xs: '90%', md: '90%' },
          }}
        >
          <Grid
            size={12}
            sx={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <Typography variant='h6' component='h2'>
              Edit Skilling Sheet - {entry.name}
            </Typography>
            <IconButton
              sx={{
                color: 'black',
                '&:hover': {
                  color: 'var(--arcturus-red)',
                },
              }}
              onClick={handleClose}
            >
              <CloseIcon />
            </IconButton>
          </Grid>
          <Grid size={12}>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={
                  <DeleteIcon sx={{ color: 'var(--arcturus-red)' }} />
                }
                sx={{ marginBottom: '20px' }}
                disabled={!entry.california}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'california']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'california'),
                    );
                  }
                }}
                checked={deleteSelected.includes('california')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'California'}</InputLabel>
                <Select
                  label={'California'}
                  value={formState.california || ''}
                  onChange={e =>
                    setFormState({
                      ...formState,
                      california: e.target.value,
                    })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={
                  <DeleteIcon sx={{ color: 'var(--arcturus-red)' }} />
                }
                sx={{ marginBottom: '20px' }}
                disabled={!entry.colorado}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'colorado']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'colorado'),
                    );
                  }
                }}
                checked={deleteSelected.includes('colorado')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'Colorado'}</InputLabel>
                <Select
                  label={'Colorado'}
                  value={formState.colorado || ''}
                  onChange={e =>
                    setFormState({
                      ...formState,
                      colorado: e.target.value,
                    })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={
                  <DeleteIcon sx={{ color: 'var(--arcturus-red)' }} />
                }
                sx={{ marginBottom: '20px' }}
                disabled={!entry.kentucky}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'kentucky']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'kentucky'),
                    );
                  }
                }}
                checked={deleteSelected.includes('kentucky')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'Kentucky'}</InputLabel>
                <Select
                  label={'Kentucky'}
                  value={formState.kentucky || ''}
                  onChange={e =>
                    setFormState({ ...formState, kentucky: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={
                  <DeleteIcon sx={{ color: 'var(--arcturus-red)' }} />
                }
                sx={{ marginBottom: '20px' }}
                disabled={!entry.mississippi}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'mississippi']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'mississippi'),
                    );
                  }
                }}
                checked={deleteSelected.includes('mississippi')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'Mississippi'}</InputLabel>
                <Select
                  label={'Mississippi'}
                  value={formState.mississippi || ''}
                  onChange={e =>
                    setFormState({ ...formState, mississippi: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={
                  <DeleteIcon sx={{ color: 'var(--arcturus-red)' }} />
                }
                sx={{ marginBottom: '20px' }}
                disabled={!entry.missouri}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'missouri']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'missouri'),
                    );
                  }
                }}
                checked={deleteSelected.includes('missouri')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'Missouri'}</InputLabel>
                <Select
                  label={'Missouri'}
                  value={formState.missouri || ''}
                  onChange={e =>
                    setFormState({ ...formState, missouri: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={
                  <DeleteIcon sx={{ color: 'var(--arcturus-red)' }} />
                }
                sx={{ marginBottom: '20px' }}
                disabled={!entry.northCarolina}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'northCarolina']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'northCarolina'),
                    );
                  }
                }}
                checked={deleteSelected.includes('northCarolina')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'North Carolina'}</InputLabel>
                <Select
                  label={'North Carolina'}
                  value={formState.northCarolina || ''}
                  onChange={e =>
                    setFormState({
                      ...formState,
                      northCarolina: e.target.value,
                    })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
          </Grid>
          <Grid
            size={12}
            sx={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <Tooltip
              title={'This will reset all fields to inital values.'}
              arrow
              placement='top'
            >
              <span>
                <Button
                  variant='contained'
                  sx={{
                    backgroundColor: 'var(--aldebaran-yellow)',
                    color: 'black',
                  }}
                  disabled={!hasChanges() || isSubmitting}
                  onClick={handleReset}
                >
                  Reset
                </Button>
              </span>
            </Tooltip>
            <Tooltip
              title='This will remove any skill selection from selected fields.'
              arrow
              placement='top'
            >
              <span>
                <Button
                  variant='contained'
                  sx={{
                    backgroundColor: 'var(--arcturus-red)',
                  }}
                  onClick={removeSkills}
                  disabled={!deleteSelected.length || isSubmitting}
                >
                  Remove Skills
                </Button>
              </span>
            </Tooltip>
            <Button
              disabled={!hasChanges() || isSubmitting}
              variant='contained'
              sx={{ backgroundColor: 'var(--vega-blue)' }}
              onClick={submit}
            >
              {'Submit'}
            </Button>
          </Grid>
        </Grid>
      </Modal>
      <Dialog open={isDialogOpen} onClose={hideDialog}>
        <DialogTitle>Are you sure you want to remove skills?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This action will reset the selection for the selected skills
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <Button
            onClick={hideDialog}
            variant='contained'
            sx={{
              backgroundColor: 'var(--aldebaran-yellow)',
              color: 'black',
            }}
          >
            No
          </Button>
          <Button
            onClick={removeSkillsConfirmed}
            variant='contained'
            sx={{
              backgroundColor: 'var(--arcturus-red)',
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
